<script>
import { defineAsyncComponent, ref, computed } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMetricaAccess } from 'dashboard/composables/useMetricaAccess';
import { useMetricaAiConfig } from 'dashboard/composables/useMetricaAiConfig';
import { useWindowSize } from '@vueuse/core';

const MetricaAccessWall = defineAsyncComponent(
  () => import('./metrica/MetricaAccessWall.vue')
);
const MetricaAiSetupBanner = defineAsyncComponent(
  () => import('./metrica/MetricaAiSetupBanner.vue')
);

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components-next/call/FloatingCallWidget.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import { useCallsStore } from 'dashboard/stores/calls';

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    FloatingCallWidget,
    MobileSidebarLauncher,
    MetricaAccessWall,
    MetricaAiSetupBanner,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const {
      accessBlocked,
      status: metricaStatus,
      fetchAccess,
    } = useMetricaAccess();
    const { fetchAiConfig } = useMetricaAiConfig();
    const { width: windowWidth } = useWindowSize();
    const callsStore = useCallsStore();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      metricaAccessBlocked: accessBlocked,
      metricaStatus,
      fetchMetricaAccess: fetchAccess,
      fetchMetricaAiConfig: fetchAiConfig,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.fetchMetricaAccess();
    this.fetchMetricaAiConfig();
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <!-- [metrica] metrica-chrome is what makes the gutter around <main> continuous
       with the sidebar's own fill. Without it the sidebar becomes chrome while
       the gutter stays --background-color, and the sidebar reads as a card
       again — a light one on black. -->
  <div
    class="flex flex-grow overflow-hidden text-n-slate-12 bg-n-background metrica-chrome"
  >
    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main
      class="flex flex-1 min-h-0 w-full overflow-hidden bg-n-surface-1 md:m-2 md:rounded-xl md:border md:border-n-weak"
    >
      <div class="flex flex-col flex-1 min-h-0 w-full">
        <MetricaAiSetupBanner v-if="!metricaAccessBlocked" />
        <div class="flex flex-1 min-h-0 w-full">
          <UpgradePage
            v-show="showUpgradePage"
            ref="upgradePageRef"
            :bypass-upgrade-page="bypassUpgradePage"
          >
            <MobileSidebarLauncher
              :is-mobile-sidebar-open="isMobileSidebarOpen"
              @toggle="toggleMobileSidebar"
            />
          </UpgradePage>
          <template v-if="!showUpgradePage">
            <router-view />
            <CommandBar />
            <CopilotLauncher />
            <MobileSidebarLauncher
              :is-mobile-sidebar-open="isMobileSidebarOpen"
              @toggle="toggleMobileSidebar"
            />
            <CopilotContainer />
            <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
          </template>
        </div>
      </div>
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
    <MetricaAccessWall v-if="metricaAccessBlocked" :status="metricaStatus" />
  </div>
</template>
